// src/components/timeslot/AddTimeSlotModal.tsx
import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Modal,
    TextField,
    Typography
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import "./TimeSlotModal.css"
import { TimeSlot, newTimeSlot } from "../../data/timeSlots";
import { updateTimeSlot } from "../../services/scheduleService";
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";


// Use Dayjs for date fields in form state
interface TimeSlotForm extends Omit<TimeSlot, 'StartsAt' | 'EndsAt' | 'TimeSlotId'> {
  StartsAt: Dayjs;
  EndsAt: Dayjs;
}


interface AddTimeSlotModalProps {
    isOpen: boolean;
    initialDate: Date;
    onClose: () => void;
    eventId: string;
    eventStart: string;
    eventEnd:   string;
}

const modalStyle = {};

const AddTimeSlotModal: React.FC<AddTimeSlotModalProps> = ({
    isOpen,
    initialDate,
    onClose,
    eventId,
    eventStart,
    eventEnd
}) => {
    const [slot, setSlot] = useState<TimeSlotForm>(() => {
        const defaultDate = dayjs(initialDate).hour(12).minute(0).second(0);
        return {
            Title:   "",
            EventId: eventId,
            StartsAt: defaultDate,
            EndsAt:   defaultDate,
        };
    });
    const [errors, setErrors] = useState({
        Title: "",
        StartsAt: "",
        EndsAt: ""
    });
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    useEffect(() => {
        console.log("Modal got initialDate:", initialDate.toISOString(), "isOpen:", isOpen)
        if (!isOpen) return;
        const defaultDate = dayjs(initialDate).hour(12).minute(0).second(0);
        setSlot({
            Title:   "",
            EventId: eventId,
            StartsAt: defaultDate,
            EndsAt:   defaultDate,
        });
        setErrors({ Title: "", StartsAt: "", EndsAt: "" });
        setSubmitError(null);
    }, [isOpen, eventId, initialDate]);  // ← include initialDate here

    const validate = () => {
        const errs = { Title: "", StartsAt: "", EndsAt: "" };
        if (!slot.Title.trim()) errs.Title = "Title is required.";
        if (!slot.StartsAt) errs.StartsAt = "Start time is required.";
        if (!slot.EndsAt) errs.EndsAt = "End time is required.";
        if (slot.StartsAt && slot.EndsAt && slot.StartsAt >= slot.EndsAt)
            errs.EndsAt = "End must be after start.";
        setErrors(errs);
        return !Object.values(errs).some(Boolean);
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        setSubmitError(null);
        try {
            const createPayload = ({
              Title: slot.Title,
              StartsAt: slot.StartsAt.toISOString(),
              EndsAt: slot.EndsAt.toISOString(),
              EventId: slot.EventId,
            });
            await updateTimeSlot(createPayload);
            onClose();
        } catch (e: any) {
            setSubmitError(e.message || "Failed to add time slot.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={isOpen} onClose={onClose} aria-labelledby="add-timeslot">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="modalBox">
                <Typography variant="h6">Add Time Slot</Typography>

                <TextField
                    className="formField"
                    sx={{ mb: 2 }}
                    fullWidth
                    label="Title"
                    name="Title"
                    value={slot.Title}
                    onChange={(e) =>
                        setSlot((s) => ({ ...s, Title: e.target.value }))
                    }
                    error={!!errors.Title}
                    helperText={errors.Title}
                />

                <DateTimePicker
                    className="formField"
                    sx={{ mb: 2 }}
                    label="Starts At"
                    value={slot.StartsAt}
                    onChange={(dt) =>
                        setSlot((s) => ({ ...s, StartsAt: dt || s.StartsAt }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.StartsAt,
                        helperText: errors.StartsAt,
                      }
                    }}
                />

                <DateTimePicker
                    className="formField"
                    sx={{ mb: 2 }}
                    label="Ends At"
                    value={slot.EndsAt}
                    onChange={(dt) =>
                        setSlot((s) => ({ ...s, EndsAt: dt || s.EndsAt }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.EndsAt,
                        helperText: errors.EndsAt,
                      }
                    }}
                />

                {submitError && (
                    <Typography className="errorText">{submitError}</Typography>
                )}

                <Box className="actions">
                    <Button onClick={onClose} sx={{ mr: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        className="editOrAdd"
                        disabled={loading}
                    >
                        {loading ? "Submitting..." : "Submit"}
                    </Button>
                </Box>
            </Box>
            </LocalizationProvider>

        </Modal>
    );
};

export default AddTimeSlotModal;